// a page's <title> and the Open Graph tags link previews are built from
// (iMessage, WhatsApp, Slack, Discord, etc). many of those apps take the
// title only from `og:title`, ignoring the page's <title>.
//
// shared by the server, which renders these into a page's HTML, and the
// client, which updates them as people navigate, so both produce the same
// tags for the same page.

// how the site names itself in page titles and link previews.
export const SITE_NAME = 'Links Club ⛳️';

export interface PageMetadata {
  title: string;
  description?: string;
  imageUrl?: string;
  type?: 'music.song' | 'music.album' | 'music.playlist';
}

export function renderPageTitle(pageTitle?: string): string {
  return pageTitle ? `${pageTitle} | ${SITE_NAME}` : SITE_NAME;
}

// a title naming the artist too, e.g. "Mirage · Camel". falls back to just
// the name when the artist isn't known.
export function titleWithArtist(name: string, artistName?: string | null) {
  return artistName ? `${name} · ${artistName}` : name;
}

// Apple artwork URLs are templates with `{w}` and `{h}` size placeholders.
function previewImageUrl(artworkUrl?: string | null) {
  return artworkUrl?.replace('{w}', '400').replace('{h}', '400');
}

export function songPageMetadata({
  songName,
  artistName,
  albumName,
  artworkUrl,
}: {
  songName: string;
  artistName?: string | null;
  albumName: string;
  artworkUrl?: string | null;
}): PageMetadata {
  return {
    title: titleWithArtist(songName, artistName),
    description: albumName,
    imageUrl: previewImageUrl(artworkUrl),
    type: 'music.song',
  };
}

export function albumPageMetadata({
  albumName,
  artistName,
  artworkUrl,
}: {
  albumName: string;
  artistName?: string | null;
  artworkUrl?: string | null;
}): PageMetadata {
  return {
    title: titleWithArtist(albumName, artistName),
    imageUrl: previewImageUrl(artworkUrl),
    type: 'music.album',
  };
}

// playlists have no single artist, so their previews use just the name.
export function playlistPageMetadata({
  playlistName,
  artworkUrl,
}: {
  playlistName: string;
  artworkUrl?: string | null;
}): PageMetadata {
  return {
    title: playlistName,
    imageUrl: previewImageUrl(artworkUrl),
    type: 'music.playlist',
  };
}
